/**
 * HistoryHeader Component
 * Header for history view with title, timestamp range, and pagination controls
 */

import { useMemo } from 'react';
import { Music, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import type { PlayHistory } from '@/stores/history';

interface HistoryHeaderProps {
  items: PlayHistory[];
  cursors: {
    before: string | null;
    after: string | null;
  };
  onRefresh: () => void;
  isRefreshing: boolean;
}

/**
 * Format timestamp to relative time (e.g., "2 hours ago")
 */
function formatRelativeTime(timestamp: string): string {
  try {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSeconds < 60) {
      return 'just now';
    } else if (diffMinutes < 60) {
      return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString();
    }
  } catch (error) {
    return timestamp;
  }
}

/**
 * History header with pagination and metadata
 */
export function HistoryHeader({
  items,
  cursors: _cursors, // Prefixed with _ to indicate intentionally unused for now
  onRefresh,
  isRefreshing,
}: HistoryHeaderProps) {
  // Calculate timestamp range from items
  const timestampRange = useMemo(() => {
    if (items.length === 0) {
      return null;
    }

    // Items are ordered by playedAt descending (most recent first)
    const mostRecent = items[0]?.playedAt;
    const oldest = items[items.length - 1]?.playedAt;

    if (!mostRecent || !oldest) {
      return null;
    }

    return {
      mostRecent: formatRelativeTime(mostRecent),
      oldest: formatRelativeTime(oldest),
    };
  }, [items]);

  // Note: Pagination not yet implemented in the hook
  // This is placeholder UI for future cursor-based navigation
  const canGoBack = false; // cursors.before !== null
  const canGoForward = false; // cursors.after !== null

  return (
    <div className="p-6 border-b border-zinc-800 bg-zinc-950">
      {/* Title Row */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Music className="w-6 h-6 text-emerald-500" />
          <div>
            <h2 className="text-xl font-semibold text-zinc-100">Listening History</h2>
            {timestampRange && (
              <p className="text-sm text-zinc-500 mt-1">
                From {timestampRange.oldest} to {timestampRange.mostRecent}
              </p>
            )}
          </div>
        </div>

        {/* Refresh Button */}
        <button
          onClick={onRefresh}
          disabled={isRefreshing}
          className="flex items-center gap-2 px-3 py-2 bg-zinc-800 hover:bg-zinc-700 disabled:bg-zinc-800 disabled:opacity-50 text-zinc-200 rounded-lg transition-colors"
          title="Refresh history"
        >
          <RefreshCw
            className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`}
          />
          <span className="text-sm">Refresh</span>
        </button>
      </div>

      {/* Info Row with Pagination Controls */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-zinc-400">
          Showing {items.length} most recent tracks
        </div>

        {/* Pagination Controls (disabled for now) */}
        <div className="flex items-center gap-2">
          <button
            disabled={!canGoBack}
            className="p-2 bg-zinc-800 hover:bg-zinc-700 disabled:bg-zinc-800 disabled:opacity-30 text-zinc-300 rounded-lg transition-colors"
            title="Previous page"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            disabled={!canGoForward}
            className="p-2 bg-zinc-800 hover:bg-zinc-700 disabled:bg-zinc-800 disabled:opacity-30 text-zinc-300 rounded-lg transition-colors"
            title="Next page"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
